import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AdminLayout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { motion, Reorder } from "framer-motion";
import {
  Plus, Trash2, Save, Sparkles, GripVertical, ArrowUpDown,
  Wand2, Loader2, Settings, ListChecks, RotateCcw
} from "lucide-react";

export default function AdminTasks() {
  const [tasks, setTasks] = useState([]);
  const [settings, setSettings] = useState({ daily_limit: 10, onboarding_enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [refining, setRefining] = useState(false);
  const [rawPrompt, setRawPrompt] = useState("");
  const [generateCount, setGenerateCount] = useState(10);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [showSettings, setShowSettings] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [tRes, sRes] = await Promise.all([
        api.get("/admin/tasks"),
        api.get("/admin/tasks/settings"),
      ]);
      setTasks(tRes.data || []);
      setSettings(sRes.data || { daily_limit: 10, onboarding_enabled: true });
    } catch (e) {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/tasks/settings", settings);
      toast.success("Task settings saved");
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const addTask = async () => {
    try {
      const r = await api.post("/admin/tasks", {
        title: "New Task",
        description: "Describe what the worker should do.",
        order: tasks.length,
        is_active: true,
      });
      setTasks(prev => [...prev, r.data]);
      toast.success("Task added");
    } catch (e) {
      toast.error("Failed to add task");
    }
  };

  const updateTask = async (id) => {
    try {
      await api.patch(`/admin/tasks/${id}`, editForm);
      setTasks(prev => prev.map(t => t.id === id ? { ...t, ...editForm, updated_at: new Date().toISOString() } : t));
      setEditing(null);
      toast.success("Task updated");
    } catch (e) {
      toast.error("Failed to update task");
    }
  };

  const deleteTask = async (id) => {
    if (!confirm("Delete this task?")) return;
    try {
      await api.delete(`/admin/tasks/${id}`);
      setTasks(prev => prev.filter(t => t.id !== id));
      toast.success("Task deleted");
    } catch (e) {
      toast.error("Failed to delete task");
    }
  };

  const handleReorder = async (newOrder) => {
    setTasks(newOrder);
    try {
      await api.post("/admin/tasks/reorder", { task_ids: newOrder.map(t => t.id) });
    } catch (e) {
      toast.error("Reorder failed");
      load();
    }
  };

  const toggleActive = async (id, current) => {
    try {
      await api.patch(`/admin/tasks/${id}`, { is_active: !current });
      setTasks(prev => prev.map(t => t.id === id ? { ...t, is_active: !current } : t));
    } catch (e) {
      toast.error("Toggle failed");
    }
  };

  const handleGenerate = async () => {
    if (!rawPrompt.trim()) { toast.error("Enter a prompt first"); return; }
    setGenerating(true);
    try {
      const r = await api.post("/admin/tasks/generate", { raw_prompt: rawPrompt, count: generateCount });
      const created = r.data.tasks || [];
      setTasks(prev => [...prev, ...created]);
      toast.success(`Generated ${created.length} tasks`);
      setRawPrompt("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleRefine = async () => {
    if (tasks.length === 0) { toast.error("No tasks to refine"); return; }
    setRefining(true);
    try {
      const r = await api.post("/admin/tasks/refine", { tasks: tasks.map(t => ({ title: t.title, description: t.description })) });
      const refined = r.data.tasks || [];
      const updated = tasks.map((t, i) => refined[i] ? { ...t, title: refined[i].title, description: refined[i].description } : t);
      // save each refined task back to DB
      for (let i = 0; i < updated.length; i++) {
        if (refined[i]) {
          await api.patch(`/admin/tasks/${updated[i].id}`, { title: refined[i].title, description: refined[i].description });
        }
      }
      setTasks(updated);
      toast.success("Tasks refined and saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refinement failed");
    } finally {
      setRefining(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-[#A1B4A8]">Loading task management…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8 max-w-5xl">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-overline text-[#D4AF37]">Workflow</div>
            <h1 className="font-display text-4xl mt-2">Daily Tasks</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="border-[#21362A] text-[#A1B4A8] hover:text-white" onClick={() => setShowSettings(s => !s)} data-testid="toggle-task-settings">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </Button>
            <Button variant="outline" className="border-[#21362A] text-[#A1B4A8] hover:text-white" onClick={handleRefine} disabled={refining} data-testid="refine-all-btn">
              <Wand2 className="w-4 h-4 mr-2" /> {refining ? "Refining…" : "Refine All"}
            </Button>
            <Button className="bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07] font-semibold" onClick={addTask} data-testid="add-task-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Task
            </Button>
          </div>
        </div>

        {showSettings && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
              <CardHeader>
                <CardTitle className="font-display text-lg">Task Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="label-overline">Daily Task Limit</Label>
                    <Input
                      type="number"
                      min={1}
                      max={50}
                      value={settings.daily_limit}
                      onChange={e => setSettings(s => ({ ...s, daily_limit: Number(e.target.value) }))}
                      className="bg-[#132018] border-[#21362A]"
                      data-testid="daily-limit-input"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 border border-[#21362A] rounded-sm bg-[#050A07]">
                    <Label className="label-overline">Show Onboarding</Label>
                    <Switch
                      checked={settings.onboarding_enabled}
                      onCheckedChange={v => setSettings(s => ({ ...s, onboarding_enabled: v }))}
                      data-testid="onboarding-toggle"
                    />
                  </div>
                </div>
                <Button className="bg-[#097969] hover:bg-[#0a8a78] text-white" onClick={saveSettings} disabled={saving} data-testid="save-task-settings">
                  <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Settings"}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#D4AF37]" /> AI Generate Tasks
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              placeholder="Describe what workers should do today. The AI will turn this into clean, short task sentences."
              value={rawPrompt}
              onChange={e => setRawPrompt(e.target.value)}
              className="bg-[#132018] border-[#21362A] min-h-[80px]"
              data-testid="ai-generate-prompt"
            />
            <div className="flex items-center gap-3 flex-wrap">
              <div className="space-y-1">
                <Label className="text-xs text-[#A1B4A8]">Count</Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={generateCount}
                  onChange={e => setGenerateCount(Number(e.target.value))}
                  className="w-24 bg-[#132018] border-[#21362A]"
                  data-testid="generate-count-input"
                />
              </div>
              <div className="flex-1" />
              <Button
                className="bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07] font-semibold"
                onClick={handleGenerate}
                disabled={generating}
                data-testid="ai-generate-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {generating ? "Generating…" : "Generate Tasks"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[#A1B4A8] text-sm">
            <ListChecks className="w-4 h-4" />
            <span>{tasks.filter(t => t.is_active).length} active tasks</span>
            <span className="text-[#21362A]">|</span>
            <span>{tasks.filter(t => !t.is_active).length} inactive</span>
          </div>

          <Reorder.Group axis="y" values={tasks} onReorder={handleReorder} className="space-y-3">
            {tasks.map((task) => (
              <Reorder.Item key={task.id} value={task}>
                <motion.div layout>
                  <Card className={`bg-[#0C140F] border rounded-sm ${task.is_active ? "border-[#21362A]" : "border-[#21362A]/40 opacity-60"}`}>
                    <CardContent className="p-4 flex items-start gap-3">
                      <div className="mt-1 cursor-grab text-[#A1B4A8]/50 hover:text-[#A1B4A8]">
                        <GripVertical className="w-4 h-4" />
                      </div>

                      {editing === task.id ? (
                        <div className="flex-1 space-y-2">
                          <Input
                            value={editForm.title}
                            onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                            className="bg-[#132018] border-[#21362A]"
                            placeholder="Task title"
                            data-testid={`edit-title-${task.id}`}
                          />
                          <Textarea
                            value={editForm.description}
                            onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                            className="bg-[#132018] border-[#21362A] min-h-[60px]"
                            placeholder="Short description"
                            data-testid={`edit-desc-${task.id}`}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-[#097969] hover:bg-[#0a8a78] text-white" onClick={() => updateTask(task.id)} data-testid={`save-task-${task.id}`}>
                              <Save className="w-3 h-3 mr-1" /> Save
                            </Button>
                            <Button size="sm" variant="ghost" className="text-[#A1B4A8]" onClick={() => setEditing(null)} data-testid={`cancel-edit-${task.id}`}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-white font-display text-base">{task.title}</h3>
                            {!task.is_active && (
                              <span className="text-[10px] uppercase tracking-wider text-[#A1B4A8] bg-[#132018] px-1.5 py-0.5 rounded-sm">Inactive</span>
                            )}
                          </div>
                          <p className="text-sm text-[#A1B4A8]">{task.description}</p>
                        </div>
                      )}

                      {editing !== task.id && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-[#A1B4A8] hover:text-white"
                            onClick={() => { setEditing(task.id); setEditForm({ title: task.title, description: task.description }); }}
                            data-testid={`edit-task-${task.id}`}
                          >
                            Edit
                          </Button>
                          <Switch
                            checked={task.is_active}
                            onCheckedChange={() => toggleActive(task.id, task.is_active)}
                            data-testid={`toggle-active-${task.id}`}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-[#E63946] hover:text-[#ff6b75]"
                            onClick={() => deleteTask(task.id)}
                            data-testid={`delete-task-${task.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Reorder.Item>
            ))}
          </Reorder.Group>
        </div>
      </div>
    </AdminLayout>
  );
}
